import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { Icon, Logo } from '../ui';
import { LEGAL_URL, LegalLinks } from './Legal';

export function Login() {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/v1/auth/magic-link', {
        method: 'POST',
        json: { email: email.trim(), ...(mode === 'signup' ? { account_name: accountName.trim(), accept_terms: accepted } : {}) },
      });
      setSentTo(email.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="center">
      <div className="auth">
        <div className="brand">
          <Logo />
        </div>
        {sentTo ? (
          <div className="card">
            <div className="row" style={{ gap: 12, color: 'var(--accent)' }}>
              <Icon name="mail" size={22} />
              <h1>Check your inbox</h1>
            </div>
            <p className="muted" style={{ margin: 0 }}>
              We sent a sign-in link to <b style={{ color: 'var(--text)' }}>{sentTo}</b>. It works once and expires in 15 minutes.
            </p>
            <p className="faint" style={{ margin: 0, fontSize: 13 }}>
              Nothing there? Check spam, or{' '}
              <button type="button" className="link" onClick={() => setSentTo(null)}>
                try another address
              </button>
              .
            </p>
          </div>
        ) : (
          <div className="card">
            <div>
              <h1>{mode === 'signin' ? 'Sign in' : 'Create your account'}</h1>
              <p className="sub">
                {mode === 'signin'
                  ? 'We email you a link. No password.'
                  : 'Live video for your app or event. Every new account is reviewed before it can stream.'}
              </p>
            </div>
            <form onSubmit={submit}>
              {mode === 'signup' && (
                <label className="field">
                  Company name
                  <input className="input" required value={accountName} onChange={(e) => setAccountName(e.target.value)} maxLength={80} placeholder="Lumo Yoga Ltd" autoComplete="organization" />
                </label>
              )}
              <label className="field">
                Work email
                <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" autoFocus />
              </label>
              {mode === 'signup' && (
                <>
                  <label className="check legal-check">
                    <input type="checkbox" required checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                    <span>
                      I accept the{' '}
                      <a href={`${LEGAL_URL}/terms`} target="_blank" rel="noopener">Terms of Service</a>,{' '}
                      <a href={`${LEGAL_URL}/aup`} target="_blank" rel="noopener">Acceptable Use Policy</a> and{' '}
                      <a href={`${LEGAL_URL}/dpa`} target="_blank" rel="noopener">Data Processing Agreement</a> on behalf of my company.
                    </span>
                  </label>
                  <p className="faint" style={{ margin: 0, fontSize: 13 }}>
                    See our <a href={`${LEGAL_URL}/privacy`} target="_blank" rel="noopener">Privacy Policy</a>.
                  </p>
                </>
              )}
              {error && <p className="error-text" role="alert" style={{ margin: 0 }}>{error}</p>}
              <button className="btn primary" type="submit" disabled={busy || (mode === 'signup' && !accepted)} style={{ height: 40 }}>
                {busy ? 'Sending…' : mode === 'signin' ? 'Email me a sign-in link' : 'Create account'}
              </button>
            </form>
          </div>
        )}
        {!sentTo && (
          <p className="foot">
            {mode === 'signin' ? (
              <>
                New to JustOnAir?{' '}
                <button type="button" className="link" onClick={() => setMode('signup')}>
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{' '}
                <button type="button" className="link" onClick={() => setMode('signin')}>
                  Sign in
                </button>
              </>
            )}
          </p>
        )}
        <LegalLinks />
      </div>
    </div>
  );
}
